import React, { useEffect, useState, useCallback } from "react";
import { useRefreshRead } from "@/hooks/useRefreshRead";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { SourceBadge } from "@/components/v3/SourceBadge";
import { LoadingState, EmptyState, ErrorState } from "@/components/v3/States";
import { Database, Lock, PenLine } from "lucide-react";

function HistoricalPanel({ onLoaded }) {
  const [collections, setCollections] = useState([]);
  const [coll, setColl] = useState("applicants");
  const [rec, setRec] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    api.get("/v3/legacy/collections").then((r) => setCollections(r.data)).catch(() => {});
  }, []);

  const fetchLatest = useCallback(() => {
    setBusy(true); setErr(null); setRec(null);
    api.get(`/v3/legacy/${coll}/latest`)
      .then((r) => { const d = r.data.records[0]; setRec(d); onLoaded(coll, d); })
      .catch((e) => setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message))
      .finally(() => setBusy(false));
  }, [coll, onLoaded]);
  useRefreshRead(() => { if (rec) fetchLatest(); });

  return (
    <div className="bg-white border border-slate-200 rounded-sm" data-testid="historical-panel">
      <div className="px-5 py-3.5 border-b border-slate-200 flex items-center gap-2">
        <Lock size={14} className="text-slate-400" />
        <span className="font-semibold text-slate-900 text-sm">Historical read — production MongoDB</span>
        <SourceBadge source="HISTORICAL" className="ml-auto" />
      </div>
      <div className="p-5">
        <div className="flex items-center gap-2 mb-4 flex-wrap">
          <select data-testid="legacy-collection-select" value={coll} onChange={(e) => setColl(e.target.value)}
            className="border border-slate-300 rounded px-2.5 py-1.5 text-sm bg-white">
            {(collections.length ? collections : [{ name: "applicants", label: "" }]).map((c) => (
              <option key={c.name} value={c.name}>{c.name}</option>
            ))}
          </select>
          <button data-testid="fetch-legacy-btn" onClick={fetchLatest}
            className="bg-[#b3122a] text-white text-sm font-semibold rounded px-3.5 py-1.5 hover:bg-[#8f0e21] transition-colors">
            Read latest record
          </button>
          <span className="text-[11px] text-slate-400">Read-only credential · KYC fields masked · never bulk-copied</span>
        </div>
        {busy && <LoadingState label="Reading from Atlas via static-IP proxy…" />}
        {err && <ErrorState message={err} onRetry={fetchLatest} />}
        {!busy && !err && !rec && <EmptyState title="No record loaded" detail="Choose a collection and read the latest record safely." />}
        {!busy && rec && (
          <div className="border border-slate-200 rounded max-h-96 overflow-auto" data-testid="legacy-record-view">
            <table className="w-full table-fixed text-xs">
              <tbody>
                {Object.entries(rec).slice(0, 60).map(([k, v]) => (
                  <tr key={k} className="border-b border-slate-100 last:border-0">
                    <td className="px-3 py-1.5 font-mono text-slate-500 break-all align-top">{k}</td>
                    <td className="px-3 py-1.5 text-slate-800 break-all">
                      {typeof v === "object" && v !== null ? JSON.stringify(v).slice(0, 160) : String(v ?? "—").slice(0, 160)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function V3WritePanel({ linked, onWritten, datastore }) {
  const [title, setTitle] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = () => {
    if (!title.trim()) { toast.error("Give the record a title"); return; }
    setBusy(true);
    const payload = { title, note };
    if (linked?.id) {
      payload.legacy_collection = linked.collection;
      payload.legacy_mongo_id = linked.id;
      payload.legacy_loan_id = linked.loanId == null ? null : String(linked.loanId);
    }
    api.post("/v3/records", payload)
      .then((r) => { toast.success(`HQ record saved (${r.data.record.v3_id.slice(0, 8)}…)`); setTitle(""); setNote(""); onWritten(); })
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message))
      .finally(() => setBusy(false));
  };

  return (
    <div className="bg-white border border-slate-200 rounded-sm" data-testid="v3-write-panel">
      <div className="px-5 py-3.5 border-b border-slate-200 flex items-center gap-2">
        <PenLine size={14} className="text-amber-600" />
        <span className="font-semibold text-slate-900 text-sm" data-testid="v3-write-datastore">New HQ record{datastore ? ` · ${datastore.db}` : ""}</span>
        <SourceBadge source="TEST_V3" className="ml-auto" />
      </div>
      <div className="p-5 space-y-3">
        <input data-testid="v3-record-title-input" value={title} onChange={(e) => setTitle(e.target.value)}
          placeholder="Title (e.g. Foundation smoke test)"
          className="w-full border border-slate-300 rounded px-3 py-2 text-sm" />
        <textarea data-testid="v3-record-note-input" value={note} onChange={(e) => setNote(e.target.value)}
          placeholder="Note (optional)" rows={2}
          className="w-full border border-slate-300 rounded px-3 py-2 text-sm" />
        {linked?.id ? (
          <div className="text-[11px] text-slate-500 bg-slate-50 border border-slate-200 rounded px-2.5 py-1.5" data-testid="legacy-link-chip">
            Will link (not copy) to historical <b>{linked.collection}</b> · legacy_mongo_id <span className="font-mono">{linked.id.slice(0, 10)}…</span>
          </div>
        ) : (
          <div className="text-[11px] text-slate-400">No legacy link — read a historical record on the left to attach one.</div>
        )}
        <button data-testid="v3-record-submit-btn" onClick={submit} disabled={busy}
          className="bg-[#111111] text-white text-sm font-semibold rounded px-4 py-2 hover:bg-black transition-colors disabled:opacity-50">
          {busy ? "Writing…" : "Write V3 test record"}
        </button>
      </div>
    </div>
  );
}

export default function Foundation() {
  const [linked, setLinked] = useState(null);
  const [records, setRecords] = useState(null);
  const [audit, setAudit] = useState(null);
  const [datastore, setDatastore] = useState(null);
  const [error, setError] = useState(null);

  const refresh = useCallback(() => {
    setError(null);
    api.get("/v3/meta").then((r) => setDatastore(r.data.v3_datastore)).catch(() => setError("HQ datastore connection could not be verified."));
    api.get("/v3/records").then((r) => setRecords(r.data.records)).catch(() => setError("HQ records could not be loaded."));
    api.get("/v3/audit").then((r) => setAudit(r.data.events)).catch(() => setError("HQ audit trail could not be loaded."));
  }, []);
  useEffect(() => { refresh(); }, [refresh]);
  useRefreshRead(refresh);

  const onLoaded = useCallback((collection, doc) => {
    if (doc?._id) setLinked({ collection, id: doc._id, loanId: doc.applicant_no ?? doc.facilityNumber ?? null });
  }, []);

  return (
    <div className="p-6 lg:p-8 max-w-7xl" data-testid="foundation-page">
      <div className="mb-6">
        <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#b3122a] flex items-center gap-1.5"><Database size={12} /> Build 1 · Data Foundation</div>
        <h1 className="font-['Playfair_Display'] text-3xl text-slate-900 mt-1">Two datastores, one experience</h1>
        <p className="text-sm text-slate-500 mt-1.5 max-w-3xl">
          Left: a live, safely-masked read from Red Fort's production MongoDB (strictly read-only).
          Right: an HQ write in separate <span className="font-mono">v3_*</span> collections within the configured application database,
          linked back by <span className="font-mono">legacy_mongo_id</span> — the historical record is never touched.
        </p>
      </div>

      {error && <div className="mb-5" data-testid="foundation-data-error"><ErrorState message={error} onRetry={refresh} /></div>}
      <div className="border-y border-slate-300 py-4 mb-6 text-xs flex flex-wrap gap-2 items-center" data-testid="v3-datastore-status">
        <SourceBadge source="MOCK" />
        {datastore ? <><b data-testid="v3-configured-db">{datastore.db}</b><span data-testid="v3-collection-prefix">· {datastore.collection_prefix}* collections</span><span data-testid="v3-seed-status">· Demo seeding {datastore.seed_status}</span><span data-testid="v3-application-count">· {datastore.counts.applications} HQ applications</span></> : <span data-testid="v3-datastore-loading">Checking configured HQ datastore…</span>}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-6">
        <HistoricalPanel onLoaded={onLoaded} />
        <V3WritePanel linked={linked} onWritten={refresh} datastore={datastore} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div className="bg-white border border-slate-200 rounded-sm min-w-0" data-testid="v3-records-list">
          <div className="px-5 py-3.5 border-b border-slate-200 font-semibold text-slate-900 text-sm" data-testid="v3-records-heading">HQ records · isolated overlay collections</div>
          {records === null ? <LoadingState /> : records.length === 0 ? <EmptyState title="No V3 records yet" detail="Write your first test record above." /> : (
            <div className="max-h-80 overflow-auto">
              {records.map((r) => (
                <div key={r.v3_id} className="px-5 py-2.5 border-b border-slate-100 last:border-0 flex items-center gap-3" data-testid={`v3-record-${r.v3_id}`}>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold text-slate-800 truncate">{r.title}</div>
                    <div className="text-[11px] text-slate-400 truncate">
                      v3_id {r.v3_id.slice(0, 8)}… · by {r.created_by}
                      {r.legacy_ref?.legacy_mongo_id && <> · linked → {r.legacy_ref.collection}/{String(r.legacy_ref.legacy_mongo_id).slice(0, 8)}…</>}
                    </div>
                  </div>
                  <SourceBadge source="TEST_V3" />
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="bg-white border border-slate-200 rounded-sm min-w-0" data-testid="audit-trail">
          <div className="px-5 py-3.5 border-b border-slate-200 font-semibold text-slate-900 text-sm">Audit trail — every V3 write</div>
          {audit === null ? <LoadingState /> : audit.length === 0 ? <EmptyState title="No audit events" /> : (
            <div className="max-h-80 overflow-auto">
              {audit.map((a) => (
                <div key={a.v3_id} className="px-5 py-2.5 border-b border-slate-100 last:border-0 break-words" data-testid={`foundation-audit-event-${a.v3_id}`}>
                  <div className="text-sm text-slate-800">
                    <span className="font-bold text-[#b3122a]">{a.action}</span> {a.entityType} <span className="font-mono text-slate-400">{String(a.entity_v3_id).slice(0, 8)}…</span>
                  </div>
                  <div className="text-[11px] text-slate-400">
                    {a.performedBy?.email} ({a.performedBy?.role}) · {new Date(a.performedAt).toLocaleString()} · {a.details}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
